import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtEUR } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ShoppingBag, Leaf, BellRing, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { looksLikeHtml, safeHtml } from "../lib/sanitize";
import { StarRatingDisplay } from "../components/StarRating";
import ReviewsSection from "../components/ReviewsSection";
import ProductShareButtons from "../components/ProductShareButtons";
import SEO, { productJsonLd, breadcrumbJsonLd } from "../components/SEO";

function buildImgUrl(u) {
  return u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const { add } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setP(r.data); setImgIdx(0); }).catch(() => {});
    api.post(`/products/${id}/track-view`).catch(() => {});
  }, [id]);

  if (!p) return <div className="max-w-7xl mx-auto p-12 text-center text-[#4B634B]">Chargement...</div>;

  const images = (p.images && p.images.length > 0) ? p.images : (p.image_url ? [p.image_url] : []);
  const currentImg = buildImgUrl(images[imgIdx] || images[0] || "");
  const unit = p.is_promo && p.promo_price ? p.promo_price : p.price;
  const outOfStock = (p.stock ?? 0) <= 0;
  const lowStock = !outOfStock && p.stock < 5;

  const prev = () => setImgIdx((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setImgIdx((i) => (i === images.length - 1 ? 0 : i + 1));

  const handleAdd = async () => {
    if (outOfStock) return;
    if (!user) {
      toast.message("Connectez-vous pour ajouter au panier");
      navigate("/auth");
      return;
    }
    if (qty > p.stock) {
      toast.error(`Stock disponible : ${p.stock}`);
      return;
    }
    await add(p.id, qty);
    toast.success(`${p.name} ajouté au panier`);
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/products/${p.id}/notify-me`, { email: notifyEmail });
      setNotifyDone(true);
      toast.success(r.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="product-detail">
      <SEO
        seo={p.seo || {}}
        title={p.name}
        description={(p.description || "").replace(/<[^>]+>/g, "").slice(0, 200) || p.name}
        image={images[0] ? buildImgUrl(images[0]) : null}
        path={`/shop/${p.id}`}
        ogType="product"
        jsonLd={[
          productJsonLd({ product: p, urlPath: `/shop/${p.id}` }),
          breadcrumbJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Boutique", path: "/shop" },
            { name: p.name, path: `/shop/${p.id}` },
          ]),
        ]}
      />
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#4B634B] hover:text-[#7AC143] mb-8">
        <ArrowLeft className="w-4 h-4" /> Retour à la boutique
      </Link>
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-[#F2F5F0] relative group" data-testid="product-image-carousel">
            <img src={currentImg} alt={p.name} className={`w-full h-full object-cover transition-opacity duration-300 ${outOfStock ? "opacity-50" : ""}`} key={imgIdx} />
            {outOfStock && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Badge className="bg-red-500 text-white text-base py-2 px-5" data-testid="out-of-stock-overlay">Rupture de stock</Badge>
              </div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={prev} data-testid="carousel-prev" aria-label="Image précédente" className="absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={next} data-testid="carousel-next" aria-label="Image suivante" className="absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((img, i) => (
                    <button key={`dot-${img}-${i}`} onClick={() => setImgIdx(i)} aria-label={`Image ${i + 1}`} className={`w-2 h-2 rounded-full transition-all ${i === imgIdx ? "bg-[#7AC143] w-6" : "bg-white/70 hover:bg-white"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="product-thumbnails">
              {images.map((img, i) => (
                <button
                  key={`thumb-${img}-${i}`}
                  onClick={() => setImgIdx(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === imgIdx ? "border-[#7AC143]" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={buildImgUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex gap-2 mb-4">
            {p.is_limited && <Badge className="bg-[#FACC15] text-[#1A2E1A] hover:bg-[#FACC15] rounded-full">Édition limitée</Badge>}
            {p.is_promo && <Badge className="bg-[#7AC143] hover:bg-[#7AC143] rounded-full">Promo</Badge>}
            {!outOfStock && lowStock && <Badge className="bg-[#FACC15] text-[#1A2E1A] rounded-full" data-testid="badge-low-stock">Plus que {p.stock} en stock</Badge>}
            {!outOfStock && !lowStock && <Badge className="bg-[#7AC143]/10 text-[#7AC143] border border-[#7AC143]/30 rounded-full" data-testid="badge-in-stock">✓ En stock</Badge>}
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tighter text-[#1A2E1A]">{p.name}</h1>
          {(p.rating_count || 0) > 0 && (
            <a
              href="#product-reviews"
              data-testid="product-rating-summary"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('[data-testid="product-reviews"]')?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 mt-3 text-sm text-[#4B634B] hover:text-[#1A2E1A]"
            >
              <StarRatingDisplay value={p.rating_avg} size={16} />
              <span className="font-semibold text-[#1A2E1A]">{Number(p.rating_avg).toFixed(1)}</span>
              <span>· {p.rating_count} avis</span>
            </a>
          )}
          <div className="mt-4 flex items-baseline gap-3">
            {p.is_promo && p.promo_price ? (
              <>
                <span className="text-[#7AC143] font-black text-3xl">{fmtEUR(p.promo_price)}</span>
                <span className="line-through text-lg text-[#4B634B]">{fmtEUR(p.price)}</span>
              </>
            ) : (
              <span className="text-[#1A2E1A] font-black text-3xl">{fmtEUR(p.price)}</span>
            )}
          </div>
          {looksLikeHtml(p.description) ? (
            <div
              className="product-description text-[#4B634B] mt-6 leading-relaxed text-lg"
              data-testid="product-description-html"
              dangerouslySetInnerHTML={{ __html: safeHtml(p.description) }}
            />
          ) : (
            <p
              className="text-[#4B634B] mt-6 leading-relaxed text-lg whitespace-pre-line"
              data-testid="product-description-text"
            >
              {p.description}
            </p>
          )}

          {!outOfStock ? (
            <div className="mt-8 flex items-center gap-3">
              <div className="flex items-center border border-[#E5EAE5] rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="detail-decrement" className="w-10 h-10 hover:bg-[#F2F5F0] rounded-full">−</button>
                <span className="w-10 text-center font-semibold">{qty}</span>
                <button onClick={() => setQty(Math.min(p.stock, qty + 1))} data-testid="detail-increment" className="w-10 h-10 hover:bg-[#F2F5F0] rounded-full">+</button>
              </div>
              <Button onClick={handleAdd} data-testid="add-to-cart-btn" className="bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full px-8 py-6 font-bold flex-1">
                <ShoppingBag className="w-4 h-4 mr-2" /> Ajouter au panier · {fmtEUR(unit * qty)}
              </Button>
            </div>
          ) : notifyDone ? (
            <div className="mt-8 p-6 bg-[#7AC143]/10 border border-[#7AC143]/30 rounded-2xl flex items-start gap-3" data-testid="notify-confirmation">
              <CheckCircle2 className="w-5 h-5 text-[#7AC143] mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-[#1A2E1A]">Inscription confirmée</p>
                <p className="text-sm text-[#4B634B] mt-1">Vous serez notifié dès le retour en stock du produit.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleNotify} className="mt-8 p-6 bg-[#F2F5F0] border border-[#E5EAE5] rounded-2xl" data-testid="notify-form">
              <div className="flex items-start gap-3 mb-4">
                <BellRing className="w-5 h-5 text-[#7AC143] mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold text-[#1A2E1A]">Soyez le premier informé</p>
                  <p className="text-sm text-[#4B634B] mt-1">Recevez un email dès que ce produit revient en stock.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="votre@email.com"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  required
                  data-testid="notify-email-input"
                  defaultValue={user?.email || ""}
                />
                <Button type="submit" data-testid="notify-submit" className="bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full px-6 font-bold">
                  M'avertir
                </Button>
              </div>
            </form>
          )}

          <div className="mt-8 p-5 bg-[#F2F5F0] rounded-2xl border border-[#E5EAE5]">
            <div className="flex items-center gap-2 text-[#7AC143] font-bold text-sm">
              <Leaf className="w-4 h-4" /> Qualité Herbalife
            </div>
            <p className="text-sm text-[#4B634B] mt-2">Catégorie : {p.category}{!outOfStock ? ` · Stock : ${p.stock} unités` : ""}</p>
          </div>

          <ProductShareButtons product={p} productPath={`/shop/${p.id}`} />
        </div>
      </div>

      <div id="product-reviews">
        <ReviewsSection productId={p.id} />
      </div>
    </div>
  );
}
